import React from 'react';
import { Box } from '@mui/material';
import { useParams } from 'react-router-dom';

import TemplateInfo from 'components/TemplatePerformance/TemplateInfo';
import ShadowBox from 'components/ShadowBox';
import UsersAnalytics from 'components/TemplatePerformance/UsersAnalytics';
import ScreenHeading from 'components/ScreenHeading';
import LeaderBoard from 'components/Analytics/LeaderBoard';
import ScoreDistributionChart, { AttemptRateChart } from 'components/Analytics/ScoreDistribution';
import { getAverageAttemptRateDetails, getInterviewDetails, getLeaderboard } from 'services/analyticsV2';
import { WhiteBox } from 'components/AppShell';
import { InterviewVariantTypes } from 'components/Analytics/constant';

const VARIANT = InterviewVariantTypes.CASE_MOCK;

const PersonalizedCasePerformance = () => {
  const { id } = useParams();

  const [interviewDetails, setInterviewDetails] = React.useState({});
  const [interviewDetailsLoading, setInterviewDetailsLoading] = React.useState(true);
  const [interviewDetailsError, setInterviewDetailsError] = React.useState(null);

  const [leaderboardData, setLeaderboardData] = React.useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = React.useState(true);
  const [leaderboardError, setLeaderboardError] = React.useState(null);
  const [leaderboardCategory, setLeaderboardCategory] = React.useState('best_score');

  const [attemptRateData, setAttemptRateData] = React.useState(null);
  const [attemptRateLoading, setAttemptRateLoading] = React.useState(true);
  const [attemptRateError, setAttemptRateError] = React.useState(null);

  React.useEffect(() => {
    if (!id) return;
    setInterviewDetailsLoading(true);
    setInterviewDetailsError(null);

    getInterviewDetails(id, VARIANT)
      .then(response => setInterviewDetails(response || {}))
      .catch(err => setInterviewDetailsError(err.message))
      .finally(() => setInterviewDetailsLoading(false));
  }, [id]);

  React.useEffect(() => {
    if (!id) return;
    setLeaderboardLoading(true);
    setLeaderboardError(null);

    getLeaderboard({
      type: 'interview',
      id,
      interviewVariantType: VARIANT,
      leaderboardType: leaderboardCategory,
    })
      .then(response => setLeaderboardData(response || []))
      .catch(err => setLeaderboardError(err.message))
      .finally(() => setLeaderboardLoading(false));
  }, [id, leaderboardCategory]);

  React.useEffect(() => {
    if (!id) return;
    setAttemptRateLoading(true);
    setAttemptRateError(null);

    getAverageAttemptRateDetails({ type: 'interview', id, interviewVariantType: VARIANT })
      .then(response => setAttemptRateData(response))
      .catch(err => setAttemptRateError(err.message))
      .finally(() => setAttemptRateLoading(false));
  }, [id]);

  const averageScoreStats = attemptRateData?.averageScoreStats;
  const attemptRateStats = attemptRateData?.attemptRateStats;

  return (
    <Box display="flex" flexDirection="column" minHeight='calc(100vh - 76px)' gap={4}>
      <ScreenHeading title="Case interview analytics" id="case-analytics" />

      <TemplateInfo
        interviewDetails={interviewDetails}
        loading={interviewDetailsLoading}
        error={interviewDetailsError}
        interviewType={interviewDetails?.type || 'Case Mock'}
        template={interviewDetails}
      />

      <Box display='flex' gap={4}>
        <WhiteBox sx={{ width: '100% !important' }}>
          <LeaderBoard
            data={leaderboardData}
            loading={leaderboardLoading}
            error={leaderboardError}
            leaderboardCategory={leaderboardCategory}
            setLeaderboardCategory={setLeaderboardCategory}
            interviewVariantType={VARIANT}
            scoreDecimals={2}
          />
        </WhiteBox>

        <Box display='flex' width='100%' flexDirection='column' gap={4}>
          <WhiteBox>
            <ScoreDistributionChart
              // Pass the raw average through: rounding here and formatting to
              // two decimals downstream would render 3.67 as "4.00".
              avgDistribution={{ batchAverageScore: averageScoreStats?.averageScore || 0 }}
              bands={averageScoreStats?.scoreDistribution || []}
              totalStudents={attemptRateData?.totalStudents || 0}
              interviewVariantType={VARIANT}
            />
          </WhiteBox>
          <WhiteBox>
            <AttemptRateChart
              attemptRate={{
                attemptRate: attemptRateStats?.attemptRate || 0,
                userSubmittedCount: attemptRateStats?.attempted || 0,
                userNotSubmittedCount: attemptRateStats?.notAttempted || 0,
              }}
              loading={attemptRateLoading}
              error={attemptRateError}
              totalStudents={attemptRateData?.totalStudents || 0}
            />
          </WhiteBox>
        </Box>
      </Box>

      <ShadowBox sx={{ mt: 0 }}>
        <UsersAnalytics
          batch={interviewDetails?.batch?._id || interviewDetails?.batch}
          template={interviewDetails}
          forTemplateAnalytics={true}
          interviewType={VARIANT}
        />
      </ShadowBox>
    </Box>
  );
};

export default PersonalizedCasePerformance;
